// @flow

import TimelinelyPlayer, { states } from "timelinely-player/TimelinelyPlayer";
import ImageHelper from "app/util/ImageHelper";
import type { VideoSources } from "timelinely-player/TimelinelyPlayer";
import ObjectHelper from "app/util/ObjectHelper";

class VideoPlayer extends TimelinelyPlayer {
    options: any;
    urls: VideoSources;
    videoElement: HTMLVideoElement;
    durationPromise: Promise<number>;
    onChangeStateCallbacks: Array<Function>;

    init(id: ?string, urls: ?VideoSources): void {
        if (!urls) {
            throw new Error(
                "VidePlayer expects to receive the video URL/filename"
            );
        }
        this.urls = ObjectHelper.mapValues(urls, url =>
            ImageHelper.makeUrl(url)
        );
        this.onChangeStateCallbacks = [];
        this.onPlayCallbacks = [];

        this.videoElement = document.createElement("video");
        this.videoElement.controls = this.controls;
        this.videoElement.autoplay = false;

        Object.keys(this.urls).forEach(videoFileMimeType => {
            let sourceElement = document.createElement("source");
            sourceElement.src = this.urls[videoFileMimeType];
            sourceElement.type = videoFileMimeType;
            this.videoElement.appendChild(sourceElement);
        });

        this.durationPromise = new Promise((resolve, reject) => {
            let ondurationchange = () => {
                this.videoElement.removeEventListener(
                    "durationchange",
                    ondurationchange
                );
                resolve(this.videoElement.duration);
            };
            this.videoElement.addEventListener(
                "durationchange",
                ondurationchange
            );
        });

        let fireStateChanged = newState => {
            this.onChangeStateCallbacks.forEach(cb => cb({ state: newState }));
        };

        this.videoElement.onended = () => {
            fireStateChanged(states.ENDED);
        };
        this.videoElement.onpause = () => {
            fireStateChanged(states.PAUSED);
        };
        this.videoElement.onplaying = () => {
            fireStateChanged(states.PLAYING);
        };

        this.element.appendChild(this.videoElement);
    }

    play(): Promise<void> {
        return this.videoElement.play();
    }

    pause(): Promise<void> {
        this.videoElement.pause();
        return Promise.resolve();
    }

    seekTo(seconds: number): Promise<void> {
        this.videoElement.currentTime = seconds;
        return Promise.resolve();
    }

    get duration(): Promise<number> {
        return this.durationPromise;
    }

    get currentTime(): Promise<number> {
        return Promise.resolve(this.videoElement.currentTime);
    }

    get bufferedPercentage(): Promise<number> {
        return this.durationPromise.then(duration => {
            let bufferedTime = 0.0;
            let bufferesSlices = this.videoElement.buffered;
            for (let i = 0; i < bufferesSlices.length; i++) {
                bufferedTime += bufferesSlices.end(i) - bufferesSlices.start(i);
            }
            return bufferedTime / duration;
        });
    }

    get volume(): Promise<number> {
        return Promise.resolve(this.videoElement.volume);
    }

    set volume(volume: any): Promise<void> {
        this.videoElement.volume = volume;
        return Promise.resolve();
    }

    get state(): Promise<string> {
        if (this.videoElement.ended) {
            return Promise.resolve(states.ENDED);
        } else if (this.videoElement.paused) {
            return Promise.resolve(states.PAUSED);
        }
        return Promise.resolve(states.PLAYING);
    }

    get iframe(): Promise<?HTMLIFrameElement> {
        return Promise.resolve(this.videoElement);
    }

    cleanup() {
        this.onChangeStateCallbacks = [];
        this.element.removeChild(this.videoElement);
    }

    onChangeState(callback: Function): void {
        this.onChangeStateCallbacks.push(callback);
    }
}

export default VideoPlayer;



// WEBPACK FOOTER //
// ./src/timelinely-player/players/VideoPlayer.js